import { PLACEHOLDER_VALUE } from '@/constants';
import { useApiClient } from '@/hooks/useApiClient';
import type { Triple } from '@/lib/map-field-errors';
import { useAppStore } from '@/stores/app';
import type { CSVParseResponse, InstrumentRunDataRow, InstrumentRunForm } from '@/types';
import { Tooltip } from '@nautilusbio/nb-design-system';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Group } from '@nautilusbio/nb-design-system/Group';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Upload } from '@nautilusbio/nb-design-system/Upload';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { DownloadIcon, UploadCloudIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import runStyles from '../../styles.module.css';

import { createSampleTemplate, extractFile, findNearestSampleCount } from './helpers';
import { createEmptyRows, sampleManagerReducer } from './reducer';
import SampleTable from './SampleTable';
import styles from './styles.module.css';

// Maximum file size for CSV uploads (100MB)
const MAX_FILE_SIZE = 100 * 1024 * 1024;

const iconSize = 14;

type Sample = {
  name: string;
  study_name: string;
  org_id?: string;
  org_name?: string;
  well_position: string;
  demux_index: number;
};

// Helper to compare two sample arrays by the key fields used in sync logic
// Placed at module scope so it's stable and not recreated on every render.
const areSamplesEqual = (a?: InstrumentRunDataRow[] | null, b?: InstrumentRunDataRow[] | null): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((s, i) => {
    const e = b[i];
    return (
      s.name === e?.name &&
      s.org_name === e?.org_name &&
      s.study_name === e?.study_name &&
      s.well_position === e?.well_position &&
      s.demux_index === e?.demux_index &&
      s.is_control === e?.is_control &&
      s.control_type === e?.control_type
    );
  });
};

const SampleManager: FC<{
  form: InstrumentRunForm;
  noOfSamples: number;
  showCellErrors?: boolean;
  readOnlyColumns?: string[];
  showCopyToRows?: boolean;
  serverErrors?: Triple[];
  /** Fired when a single row is edited — lets the parent drop that row's stale server error. */
  onSampleRowEdit?: (rowIndex: number) => void;
  /** Fired when a bulk edit touches every row — lets the parent drop all stale server errors. */
  onSampleRowsBulkEdit?: () => void;
}> = ({
  form,
  noOfSamples,
  showCellErrors = false,
  readOnlyColumns = [],
  showCopyToRows = false,
  serverErrors,
  onSampleRowEdit,
  onSampleRowsBulkEdit,
}) => {
  const { t } = useTranslation('sampleManager');

  const initialState = useMemo(
    () => ({
      hasFileBeenUploaded: false,
      rowData:
        form.state?.values?.samples && form.state.values.samples.length > 0
          ? form.state.values.samples
          : createEmptyRows(noOfSamples),
      errorMessage: null,
      isProcessing: false,
      cellErrors: {},
    }),
    // Depend on both noOfSamples and form samples for correct initialization
    [noOfSamples, form.state?.values?.samples],
  );

  const [state, dispatch] = useReducer(sampleManagerReducer, initialState);
  const gridRef = useRef<AgGridReact<InstrumentRunDataRow>>(null);

  useEffect(() => {
    if (serverErrors === undefined || serverErrors === null) return;
    if (serverErrors.length === 0) {
      dispatch({ type: 'CLEAR_CELL_ERRORS' });
      return;
    }
    dispatch({ type: 'SET_CELL_ERRORS', payload: { triples: serverErrors } });
    const first = serverErrors[0];
    gridRef.current?.api?.ensureIndexVisible(first.row, 'middle');
    gridRef.current?.api?.refreshCells({ force: true });
  }, [serverErrors]);

  const api = useApiClient();
  const selectedOrg = useAppStore(useShallow(s => s.selectedOrganization));
  const abortControllerRef = useRef<AbortController | null>(null);
  const isUserEditingRef = useRef(false);
  // Prevents the form-sync effect from reverting the table while a resize is in-flight.
  // When noOfSamples changes, RESIZE_ROWS updates state.rowData immediately but
  // form.setFieldValue is async — the sync effect would otherwise see the mismatch
  // and overwrite the freshly-resized rows with the stale form samples.
  const isResizingRef = useRef(false);
  // Holds the expected samples array after a resize, for deterministic clearing
  const expectedResizedSamplesRef = useRef<InstrumentRunDataRow[] | null>(null);
  // Holds the expected samples array after an edit, for deterministic clearing
  const expectedEditedSamplesRef = useRef<InstrumentRunDataRow[] | null>(null);
  // Always holds the latest rowData so the noOfSamples effect can read it
  // without needing state.rowData in its dependency array (which would cause
  // the effect to re-fire on every cell edit).
  const rowDataRef = useRef(state.rowData);
  useEffect(() => {
    rowDataRef.current = state.rowData;
  }, [state.rowData]);

  // Sync form samples to SampleManager state when they differ
  // This ensures draft data and any external updates to form.samples are reflected in the table
  // CRITICAL: This effect must run whenever form data changes, including after form.reset()
  useEffect(() => {
    const formSamples = form.state?.values?.samples;

    // Skip if no form samples
    if (!formSamples) {
      return;
    }

    // If editing an existing run, allow syncing even if formSamples is empty (to clear table)
    // If creating, let UPDATE_ROW_COUNT handle empty state
    if (formSamples.length === 0 && !form.state?.values?.name) {
      return;
    }

    // Skip if user is actively editing to prevent disrupting their workflow
    if (isUserEditingRef.current) {
      return;
    }

    // Skip if a resize is in-flight — the noOfSamples effect is responsible for
    // updating both the reducer state and the form value together; letting the
    // sync effect run in between would revert state.rowData back to the old rows.
    if (isResizingRef.current) {
      return;
    }

    // Check if form samples differ from current state
    const samplesAreDifferent =
      formSamples.length !== state.rowData.length ||
      formSamples.some((sample, index) => {
        const currentRow = state.rowData[index];
        if (!currentRow) return true;

        return (
          sample.name !== currentRow.name ||
          sample.org_name !== currentRow.org_name ||
          sample.study_name !== currentRow.study_name ||
          sample.well_position !== currentRow.well_position ||
          sample.demux_index !== currentRow.demux_index ||
          sample.is_control !== currentRow.is_control ||
          sample.control_type !== currentRow.control_type
        );
      });

    // Sync if samples are different.
    // Use SYNC_FROM_FORM (not UPLOAD_SUCCESS) so hasFileBeenUploaded stays false,
    // which keeps the sample-count selector editable for draft runs.
    if (samplesAreDifferent) {
      dispatch({ type: 'SYNC_FROM_FORM', payload: { rowData: formSamples } });
    }
  }, [form.state?.values?.samples, state.rowData, form]);

  // Update row data when noOfSamples changes (only if no file has been uploaded).
  // Preserves any data the user has already entered — existing rows are kept in
  // place and only new empty rows are appended (or rows are trimmed) as needed.
  // rowDataRef is used instead of state.rowData so this effect only fires when
  // noOfSamples (or hasFileBeenUploaded) changes, not on every inline cell edit.
  useEffect(() => {
    if (!state.hasFileBeenUploaded) {
      // Mark as resizing so the form-sync effect does not clobber the new rows
      // before form.setFieldValue has propagated through TanStack Form's state machine.
      isResizingRef.current = true;

      const existingRows = rowDataRef.current;
      dispatch({
        type: 'RESIZE_ROWS',
        payload: { count: noOfSamples, existingRows },
      });

      // Mirror what the reducer does so the form value stays in sync
      const allGeneratedRows = createEmptyRows(noOfSamples);
      const mergedRows = allGeneratedRows.map((generatedRow, i) =>
        i < existingRows.length
          ? { ...existingRows[i], well_position: generatedRow.well_position, demux_index: generatedRow.demux_index }
          : generatedRow,
      );
      form.setFieldValue?.('samples', mergedRows);

      // Store the expected samples for deterministic clearing
      expectedResizedSamplesRef.current = mergedRows;
    }
  }, [noOfSamples, state.hasFileBeenUploaded, form]);

  const handleUploadSuccess = useCallback(
    (rowData: InstrumentRunDataRow[], sampleCount: number) => {
      // When in edit mode, preserve values of read-only columns from the current rows
      // so CSV upload cannot overwrite physical placement data (flow_cell, lane, etc.)
      const protectedRowData =
        readOnlyColumns.length > 0
          ? rowData.map((uploadedRow, i) => {
              const existing = rowDataRef.current[i];
              if (!existing) return uploadedRow;
              const preserved: Partial<InstrumentRunDataRow> = {};
              for (const field of readOnlyColumns) {
                const key = field as keyof InstrumentRunDataRow;
                if (key in existing) {
                  (preserved as Record<string, unknown>)[key] = existing[key];
                }
              }
              return { ...uploadedRow, ...preserved };
            })
          : rowData;

      dispatch({ type: 'UPLOAD_SUCCESS', payload: { rowData: protectedRowData } });

      // Update the form's noOfSamples field to nearest available value
      const nearestCount = findNearestSampleCount(sampleCount);

      // Update both samples and noOfSamples together in a batch to avoid validation timing issues
      form.setFieldValue?.('samples', protectedRowData);
      form.setFieldValue?.('noOfSamples', nearestCount);

      toast.success(
        t('upload_success', {
          count: sampleCount,
          plural: sampleCount !== 1 ? 's' : '',
        }),
      );
    },
    [form, t, readOnlyColumns],
  );

  const handleRowDataChange = useCallback(
    (rowData: InstrumentRunDataRow[]) => {
      // Mark that user is editing to prevent sync disruption
      isUserEditingRef.current = true;

      // Keep rowDataRef current so that if noOfSamples changes while the user
      // is editing, RESIZE_ROWS gets the latest data (not stale empty rows).
      rowDataRef.current = rowData;

      // Update form state when user edits cells inline
      form.setFieldValue?.('samples', rowData);

      // Store the expected samples for deterministic clearing
      expectedEditedSamplesRef.current = rowData;
    },
    [form],
  );

  // Stable updater to modify a single row and persist into the form values.
  // This allows cell renderers to update the authoritative form state directly
  // without relying on AG Grid events.
  const updateSampleAt = useCallback(
    (rowIndex: number, patch: Partial<InstrumentRunDataRow>) => {
      // Mark editing in progress
      isUserEditingRef.current = true;

      const current = rowDataRef.current || [];
      const updated = current.map((r, i) => (i === rowIndex ? { ...r, ...patch } : r));

      // Update reducer state so the table reflects the change immediately
      dispatch({ type: 'UPDATE_ROW', payload: { rowIndex, patch } });

      // Drop any stale backend error on this row in the parent's source-of-truth too. The
      // reducer clears its own copy via UPDATE_ROW, but the parent re-pushes serverErrors on
      // every change (e.g. when client-side dupe errors update), which would otherwise revive
      // the highlight the user just edited away.
      onSampleRowEdit?.(rowIndex);

      // Update the form values (authoritative source)
      form.setFieldValue?.('samples', updated);

      // Store expected edited samples for sync logic
      expectedEditedSamplesRef.current = updated;
    },
    [form, onSampleRowEdit],
  );

  // Applies a full-array bulk update — used by bulk operations (apply-to-all, copy-to-rows)
  // where computing all changes at once avoids the stale-closure problem of looping updateSampleAt.
  const handleBulkApply = useCallback(
    (updatedRows: InstrumentRunDataRow[]) => {
      isUserEditingRef.current = true;
      dispatch({ type: 'SYNC_FROM_FORM', payload: { rowData: updatedRows } });
      // A bulk apply rewrites every row, so any backend highlight is now stale. SYNC_FROM_FORM
      // doesn't prune cellErrors, so clear them here and have the parent drop its copy too.
      dispatch({ type: 'CLEAR_CELL_ERRORS' });
      onSampleRowsBulkEdit?.();
      rowDataRef.current = updatedRows;
      form.setFieldValue?.('samples', updatedRows);
      expectedEditedSamplesRef.current = updatedRows;
    },
    [form, onSampleRowsBulkEdit],
  );

  // Effect: Clear isUserEditingRef when form samples match expected after edit
  useEffect(() => {
    const expected = expectedEditedSamplesRef.current;
    if (isUserEditingRef.current && expected && areSamplesEqual(form.state?.values?.samples, expected)) {
      isUserEditingRef.current = false;
      expectedEditedSamplesRef.current = null;
    }
  }, [form.state?.values?.samples]);

  // Effect: Clear isResizingRef when form samples match expected after a resize.
  // Without this, isResizingRef stays true forever after the first noOfSamples change
  // (including the initial load from a draft), permanently blocking the form-sync effect
  // and making subsequent sample-count changes appear to have no effect in the UI.
  useEffect(() => {
    const expected = expectedResizedSamplesRef.current;
    if (isResizingRef.current && expected && areSamplesEqual(form.state?.values?.samples, expected)) {
      isResizingRef.current = false;
      expectedResizedSamplesRef.current = null;
    }
  }, [form.state?.values?.samples]);

  const handleUploadError = useCallback((errorMessage: string) => {
    dispatch({ type: 'UPLOAD_ERROR', payload: { errorMessage } });
    toast.error(errorMessage);
  }, []);

  const handleReset = useCallback(() => {
    // Cancel any ongoing upload
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch({ type: 'RESET' });

    // Reset both samples and noOfSamples together
    form.setFieldValue?.('samples', createEmptyRows(4));
    form.setFieldValue?.('noOfSamples', '4');
  }, [form]);

  const validateFile = useCallback(
    (file: File | Blob): string | null => {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        return t('file_too_large', { max: MAX_FILE_SIZE / (1024 * 1024) });
      }

      // Check if it's actually a file (has a name property)
      if (file instanceof File) {
        // Check file extension
        if (!file.name.toLowerCase().endsWith('.csv')) {
          return t('invalid_file_type');
        }
      }

      return null;
    },
    [t],
  );

  const uploadCsvToApi = useCallback(
    async (file: File | Blob): Promise<void> => {
      // Validate file before upload
      const validationError = validateFile(file);
      if (validationError) {
        handleUploadError(validationError);
        return;
      }

      // Create new AbortController for this upload
      abortControllerRef.current = new AbortController();

      try {
        const formData = new FormData();
        formData.append('file', file);

        // Use skipDefaultHeaders to let browser set proper Content-Type with boundary for FormData
        const response = await api.client.fetch<CSVParseResponse>(`/orgs/${selectedOrg?.id}/samples/upload`, {
          method: 'POST',
          body: formData,
          skipDefaultHeaders: true, // Browser will set multipart/form-data with boundary
          signal: abortControllerRef.current.signal,
        });

        if (!response || !response.samples) {
          handleUploadError(t('invalid_response'));
          return;
        }

        if (response.samples.length === 0) {
          handleUploadError(t('empty_csv'));
          return;
        }

        // API response samples have org_name - preserve it for display
        // The org_id will be looked up from org_name when submitting to the API
        const convertedSamples: InstrumentRunDataRow[] = response.samples.map((sample: Sample) => {
          const raw = sample as unknown as Record<string, unknown>;
          const is_control = typeof raw.is_control === 'boolean' ? raw.is_control : false;
          const control_type = typeof raw.control_type === 'string' ? (raw.control_type as string) : '';

          return {
            name: sample.name,
            study_name: sample.study_name,
            org_name: sample.org_name || '',
            org_id: '', // Will be populated from org_name on submit
            well_position: sample.well_position,
            demux_index: sample.demux_index,
            // Preserve control fields if present, otherwise default
            is_control,
            control_type,
          };
        });
        handleUploadSuccess(convertedSamples, convertedSamples.length);
      } catch (error) {
        // Don't show error if request was aborted
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        // Extract detailed error message from API error response
        let errorMessage: string = t('upload_failed');

        if (error && typeof error === 'object' && 'data' in error) {
          const apiError = error as { data?: unknown };

          // Try to extract detail from error data
          if (apiError.data && typeof apiError.data === 'object') {
            const errorData = apiError.data as Record<string, unknown>;

            // Check for detail field (common in FastAPI errors)
            if ('detail' in errorData && typeof errorData.detail === 'string') {
              errorMessage = errorData.detail;
            }
            // Check for message field
            else if ('message' in errorData && typeof errorData.message === 'string') {
              errorMessage = errorData.message;
            }
            // Check if detail is an array of validation errors
            else if ('detail' in errorData && Array.isArray(errorData.detail)) {
              const details = errorData.detail
                .map((err: unknown) => {
                  if (typeof err === 'string') return err;

                  if (
                    err &&
                    typeof err === 'object' &&
                    'msg' in err &&
                    typeof (err as { msg?: unknown }).msg === 'string'
                  ) {
                    return (err as { msg: string }).msg;
                  }

                  return null;
                })
                .filter(Boolean)
                .join('; ');

              if (details) {
                errorMessage = details;
              }
            }
          }
        } else if (error instanceof Error && error.message && !error.message.startsWith('API Error:')) {
          // Use error message if it's not the generic API error message
          errorMessage = error.message;
        }

        if (import.meta.env.DEV) console.error('CSV upload error:', error);
        handleUploadError(errorMessage);
      } finally {
        abortControllerRef.current = null;
      }
    },
    [api.client, selectedOrg, validateFile, handleUploadSuccess, handleUploadError, t],
  );

  const handleFileSelect = useCallback(
    async (fileData: unknown) => {
      dispatch({ type: 'UPLOAD_START' });

      const file = extractFile(fileData);

      if (!file) {
        const errorMsg = t('no_valid_file');
        if (import.meta.env.DEV) console.error(errorMsg, fileData);
        handleUploadError(errorMsg);
        return;
      }

      // If it's a string, we need to convert it to a Blob for the API
      if (typeof file === 'string') {
        const blob = new Blob([file], { type: 'text/csv' });
        await uploadCsvToApi(blob);
        return;
      }

      // Upload the file to the API
      await uploadCsvToApi(file);
    },
    [uploadCsvToApi, handleUploadError, t],
  );

  // Download template handler delegates CSV creation to helper
  const handleDownloadTemplate = useCallback(() => {
    // Get current form values for instrument run name and study name
    const formValues = form.state?.values;
    const instrumentRunName = formValues?.name || '';

    // Get study name from the first sample if available, since studies might vary per sample
    // If all samples should have the same study, we could make this a form-level field instead
    const samples = formValues?.samples || [];
    const studyName = samples.length > 0 && samples[0]?.study_name ? samples[0].study_name : '';

    const { blob, filename } = createSampleTemplate(
      noOfSamples,
      selectedOrg ?? undefined,
      instrumentRunName,
      studyName !== PLACEHOLDER_VALUE ? studyName : undefined, // Don't pass default placeholder value
    );

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.append(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [noOfSamples, selectedOrg, form]);

  return (
    <Stack fullWidth gap={4} orientation="vertical">
      <Stack alignItems="center" fullWidth gap={2} justifyContent="space-between" orientation="horizontal">
        <Text className={runStyles.sampleTableDesc} size="xs">
          {t('table_description')}
        </Text>

        <Group orientation="horizontal">
          <Upload
            acceptedFileTypes={['.csv']}
            buttonProps={{
              className: clsx(styles.groupedButton, styles.uploadButton),
              isPending: state.isProcessing,
              size: 'sm',
              visual: 'primary',
            }}
            buttonText={
              <>
                <UploadCloudIcon size={iconSize} />{' '}
                <Trans i18nKey="upload_csv" ns="sampleManager">
                  Upload <span>CSV</span>
                </Trans>
              </>
            }
            data-testid={'sample-manager-upload'}
            onSelect={handleFileSelect}
          />

          <Tooltip
            content={t('download_tooltip')}
            placement="top-end"
            trigger={
              <Button
                className={clsx(styles.csvButton, styles.groupedButton)}
                data-testid="sample-manager-download-template"
                onPress={handleDownloadTemplate}
                size="sm"
                visual="outline"
              >
                <DownloadIcon size={iconSize} /> {t('download_template')}
              </Button>
            }
          />

          {state.hasFileBeenUploaded && (
            <Button
              className={styles.groupedButton}
              data-testid="sample-manager-reset"
              onPress={handleReset}
              size="sm"
              visual="danger"
            >
              {t('remove_all_samples')}
            </Button>
          )}
        </Group>
      </Stack>

      {state.isProcessing && (
        <Stack alignItems="center" fullWidth gap={2} justifyContent="center" orientation="horizontal">
          <Loading type="inline" />
          <Text data-testid="csv-processing-message">{t('processing_csv')}</Text>
        </Stack>
      )}

      {state.errorMessage && (
        <Stack alignItems="center" fullWidth gap={2} justifyContent="center" orientation="horizontal">
          <Text as="h3" className={runStyles.error} data-testid="csv-error-message">
            {state.errorMessage}
          </Text>
        </Stack>
      )}

      <SampleTable
        allRowData={state.rowData}
        cellErrors={state.cellErrors}
        gridRef={gridRef}
        readOnlyColumns={readOnlyColumns}
        rowData={state.rowData}
        showCellErrors={showCellErrors}
        showCopyToRows={showCopyToRows}
        updateSampleAt={updateSampleAt}
        onBulkApply={handleBulkApply}
        onRowDataChange={handleRowDataChange}
      />
    </Stack>
  );
};

export default SampleManager;
